import { linkPayloadSchema, type LinkPayload } from './auth.schema';

/**
 * Decodes the `:token` segment of a magic link.
 *
 * The wire format is base64(encodeURIComponent(JSON)). Returns null rather than
 * throwing on anything malformed — an invalid link is an expected input here,
 * not an exceptional one, and the caller shows a "link is invalid" message.
 */
export const decodeLinkToken = (token: string): LinkPayload | null => {
  try {
    // Normalise what a URL does to base64 before decoding. A link is copied out
    // of an email client, sometimes re-encoded by a mail gateway, and lands in a
    // path segment: `+` and `/` may arrive as `-` and `_`, and the `=` padding
    // is routinely stripped or percent-escaped. Rejecting those is rejecting a
    // link that is actually valid.
    const normalised = token.replace(/%3D/gi, '=').replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalised.padEnd(Math.ceil(normalised.length / 4) * 4, '=');

    // `atob` yields one char per byte; a director with a non-ASCII name would
    // otherwise arrive mojibaked, which is why the bytes are re-decoded as UTF-8
    // instead of being read straight off the binary string.
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (char) => char.codePointAt(0) ?? 0);
    const text = new TextDecoder().decode(bytes);

    // The payload is normally URI-encoded before base64, but not every minted
    // link is. Decoding a string with a stray `%` throws, so plain JSON falls
    // back rather than failing.
    let json: string;
    try {
      json = decodeURIComponent(text);
    } catch {
      json = text;
    }

    return linkPayloadSchema.parse(JSON.parse(json));
  } catch {
    return null;
  }
};

/** Destinations an emailed `?location=` override may name. An open redirect
 *  here would let a forged link bounce a freshly-authenticated director to an
 *  attacker's page, so the parameter selects from a list rather than being
 *  used as a path. */
const ALLOWED_LOCATIONS = new Set(['companies', 'settings']);

/**
 * Where a director lands once authenticated.
 *
 * A link is minted against a specific document, so it should open that
 * document's company rather than dropping the director on a list to find it
 * again. Everything outstanding is on the dashboard, which is where a link
 * without a company goes.
 *
 * The legacy version branched on `screenWidth` to send phones into a separate
 * `/mobile/*` flow. That branch is gone: the rebuilt screens are the same at
 * 360px as at 1440px, so there is one destination per link rather than two
 * that could drift apart.
 */
export const nextRouteFor = (
  payload: LinkPayload,
  _screenWidth: number,
  search: string,
): string => {
  const requested = new URLSearchParams(search).get('location');
  if (requested !== null && ALLOWED_LOCATIONS.has(requested)) {
    return `/${requested}`;
  }

  const companyId = payload.asicCompanyId ?? '';
  if (companyId === '') return '/';

  // A form or change is reviewed under the company's Changes tab; an annual
  // review under its own. Anything else opens the company itself.
  if (payload.documentType === 'form') {
    return `/companies/${companyId}?tab=changes`;
  }
  if (payload.annualReviewId !== null && payload.annualReviewId !== undefined) {
    return `/companies/${companyId}?tab=reviews`;
  }

  return `/companies/${companyId}`;
};
